import React from 'react';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { Typography } from '@mui/material';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import qs from 'qs';
import oikosApi from '../../../services/oikos';
import powerAppsApi from '../../../services/powerapps';
import { notifyError, notifySuccess } from '../../../utils/notify';
import MaskedInput from '../../Template/Input/MaskedInput';
import Select from '../../Template/Input/Select';
import currencyFormatter from '../../../utils/currencyFormatter';
import ModalPedidoCompra, {
  EQUIPE_CATEG_GR_ID,
  fetchOrcItemsForCentroDeCusto,
  fetchPedidoCompraDocuments,
} from '../ModalPedidoCompra/ModalPedidoCompra';
import {
  COMPRA_STATUS_ABERTO,
  PEDIDO_COMPRA_COM_CONTRATO_OPTIONS,
} from '../../Template/Doc/compraCotacaoTableUtils';
import {
  buildResumoOrcamentoAnoVigente,
  buildResumoOrcamentoPorAno,
  isVigenciaDatasInvalidas,
} from '../../Template/Doc/orcResumoOrcamentarioUtils';
import DisparoEmailsCotacaoForm from '../ModalFormDisparoEmails/DisparoEmailsCotacaoForm';
import { savePedidoCompraOuVagaDocuments } from '../ModalPedidoCompra/usePedidoCompraEquipeActions';
import CotacaoRapidaPropostasSection from './CotacaoRapidaPropostasSection';
import {
  cotacaoFormTemDados,
  createEmptyCotacaoForm,
  normalizeCotacaoManualEntry,
  validateCotacaoForm,
} from './cotacaoRapidaFormUtils';
import './ModalPedidoCotacao.css';

const PEDIDO_COMPRA_STATUS_APROVADO = 5;

const TIPO_CONTRATACAO = {
  PADRAO: 'padrao',
  RAPIDA: 'rapida',
  ADESAO: 'adesao',
};

const TIPO_CONTRATACAO_LABEL = {
  [TIPO_CONTRATACAO.PADRAO]: 'Cotação padrão',
  [TIPO_CONTRATACAO.RAPIDA]: 'Cotação rápida',
  [TIPO_CONTRATACAO.ADESAO]: 'Cotação por adesão',
};

const PEDIDO_COMPRA_TP_BY_TIPO = {
  [TIPO_CONTRATACAO.PADRAO]: 2,
  [TIPO_CONTRATACAO.RAPIDA]: 3,
  [TIPO_CONTRATACAO.ADESAO]: 4,
};

const parseTipoOrdemCompra = (value) => {
  if (value === 0 || value === '0') return 0;
  if (value === 1 || value === '1') return 1;
  return null;
};

const createEmptyPedidoCompraFields = () => ({
  centroDeCustoRubr_id: null,
  orcItem_id: null,
  pedidoCompra_dtVigIni: null,
  pedidoCompra_dtVigFim: null,
});

const EMPTY_INITIAL_PEDIDO_COMPRA_DATA = Object.freeze({});
const EMPTY_SEL_ORC_ITEMS_OPTIONS = Object.freeze([]);
const EMPTY_SEL_CENTRO_DE_CUSTO = Object.freeze([]);

function ModalPedidoCotacao({
  isOpen,
  onClose = () => {},
  onSuccess = () => {},
  user = null,
  title = 'Abrir cotação',
  submitLabel = 'Abrir cotação',
  readOnly = false,
  editPedidoCompraRow = null,
  isPedidoCompra = false,
  selCentroDeCusto = EMPTY_SEL_CENTRO_DE_CUSTO,
  selOrcItemsOptions = EMPTY_SEL_ORC_ITEMS_OPTIONS,
  initialPedidoCompraData = EMPTY_INITIAL_PEDIDO_COMPRA_DATA,
  hideCentroDeCusto = false,
  readOnlyOrcItem = false,
  initialTipoContratacao = null,
  lockTipoContratacao = false,
}) {
  const resolvedInitialTipo = (
    initialTipoContratacao
    && Object.values(TIPO_CONTRATACAO).includes(initialTipoContratacao)
  )
    ? initialTipoContratacao
    : TIPO_CONTRATACAO.PADRAO;
  const showTipoRadios = !lockTipoContratacao && isPedidoCompra;

  const [descricao, setDescricao] = React.useState('');
  const [valorEstimado, setValorEstimado] = React.useState(0);
  const [tipoContratacao, setTipoContratacao] = React.useState(resolvedInitialTipo);
  const [tipoOrdemCompra, setTipoOrdemCompra] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [associarItemOrcamentario, setAssociarItemOrcamentario] = React.useState(false);
  const [pedidoCompraFields, setPedidoCompraFields] = React.useState(createEmptyPedidoCompraFields);
  const [selOrcItems, setSelOrcItems] = React.useState([]);
  const [isLoadingOrcItems, setIsLoadingOrcItems] = React.useState(false);
  const [listaDotacoes, setListaDotacoes] = React.useState([]);
  const [valoresLimites, setValoresLimites] = React.useState([]);
  const [cotacaoForm, setCotacaoForm] = React.useState(createEmptyCotacaoForm);
  const [cotacoesManuais, setCotacoesManuais] = React.useState([]);
  const disparoEmailsRef = React.useRef(null);
  const initialPedidoCompraDataRef = React.useRef(initialPedidoCompraData);
  const selOrcItemsOptionsRef = React.useRef(selOrcItemsOptions);
  const editPedidoCompraRowRef = React.useRef(editPedidoCompraRow);

  initialPedidoCompraDataRef.current = initialPedidoCompraData;
  selOrcItemsOptionsRef.current = selOrcItemsOptions;
  editPedidoCompraRowRef.current = editPedidoCompraRow;

  const showEmailSection = tipoContratacao !== TIPO_CONTRATACAO.RAPIDA;
  const showManualPropostasSection = tipoContratacao === TIPO_CONTRATACAO.RAPIDA;
  const isCotacaoRapida = tipoContratacao === TIPO_CONTRATACAO.RAPIDA;
  const skipVigenciaDates = tipoContratacao !== TIPO_CONTRATACAO.ADESAO;
  const entityLabel = isPedidoCompra ? 'pedido de compra' : 'cotação';
  const lockOrcItemAssociation = readOnlyOrcItem && Boolean(initialPedidoCompraData?.orcItem_id);
  const editPedidoCompraId = Number(editPedidoCompraRow?.pedidoCompra_id) || 0;
  const initialOrcItemId = Number(initialPedidoCompraData?.orcItem_id) || 0;
  const initialCentroDeCustoId = Number(initialPedidoCompraData?.centroDeCustoRubr_id) || 0;

  const clearOrcamentarioFields = React.useCallback(() => {
    setPedidoCompraFields(createEmptyPedidoCompraFields());
    setSelOrcItems([]);
    setIsLoadingOrcItems(false);
    setListaDotacoes([]);
    setValoresLimites([]);
  }, []);

  const resetState = React.useCallback(() => {
    setDescricao('');
    setValorEstimado(0);
    setTipoContratacao(resolvedInitialTipo);
    setTipoOrdemCompra(null);
    setIsSubmitting(false);
    setIsLoading(false);
    setAssociarItemOrcamentario(false);
    clearOrcamentarioFields();
    setCotacaoForm(createEmptyCotacaoForm());
    setCotacoesManuais([]);
    disparoEmailsRef.current?.reset?.();
  }, [resolvedInitialTipo, clearOrcamentarioFields]);

  const getOrcDots = React.useCallback(async (orcItemId) => {
    if (!orcItemId || readOnly) {
      setListaDotacoes([]);
      setValoresLimites([]);
      return;
    }

    try {
      const resOrcDots = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'orcDot',
        data: { orcItem_id: orcItemId, orcDot_ativo: 1 },
      })}`);
      setListaDotacoes(Array.isArray(resOrcDots.data) ? resOrcDots.data : []);

      const res = await powerAppsApi.post('/', {
        acao: 'verificar_disponibilidade_orcItem',
        orcItem_id: orcItemId,
      }, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'multipart/form-data',
        },
      });
      setValoresLimites(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch orcDots for pedido:', error);
      setListaDotacoes([]);
      setValoresLimites([]);
    }
  }, [readOnly]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const load = async () => {
      const currentInitialPedido = initialPedidoCompraDataRef.current || {};
      const currentSelOrcItems = selOrcItemsOptionsRef.current;
      const currentEditRow = editPedidoCompraRowRef.current;

      setIsSubmitting(false);
      setTipoContratacao(resolvedInitialTipo);
      setTipoOrdemCompra(null);
      setAssociarItemOrcamentario(false);
      setValorEstimado(0);
      clearOrcamentarioFields();
      setCotacaoForm(createEmptyCotacaoForm());
      setCotacoesManuais([]);
      disparoEmailsRef.current?.reset?.();

      const pedidoCompraId = Number(currentEditRow?.pedidoCompra_id) || 0;
      if (!pedidoCompraId) {
        setDescricao('');
        setValorEstimado(0);

        const orcItemId = Number(currentInitialPedido?.orcItem_id) || 0;
        if (orcItemId) {
          setAssociarItemOrcamentario(true);
          setPedidoCompraFields({
            centroDeCustoRubr_id: currentInitialPedido.centroDeCustoRubr_id || null,
            orcItem_id: orcItemId,
            pedidoCompra_dtVigIni: currentInitialPedido.pedidoCompra_dtVigIni || null,
            pedidoCompra_dtVigFim: currentInitialPedido.pedidoCompra_dtVigFim || null,
          });

          const presetOrcItems = Array.isArray(currentSelOrcItems) ? currentSelOrcItems : [];
          if (presetOrcItems.length > 0) {
            setSelOrcItems(presetOrcItems);
          } else if (currentInitialPedido.centroDeCustoRubr_id) {
            try {
              setIsLoadingOrcItems(true);
              const options = await fetchOrcItemsForCentroDeCusto(currentInitialPedido.centroDeCustoRubr_id);
              if (!cancelled) {
                setSelOrcItems(Array.isArray(options) ? options : []);
              }
            } catch (error) {
              if (!cancelled) {
                console.error('Erro ao carregar itens orçamentários:', error);
                setSelOrcItems([]);
              }
            } finally {
              if (!cancelled) setIsLoadingOrcItems(false);
            }
          }

          if (!cancelled) {
            await getOrcDots(orcItemId);
          }
        }
        return;
      }

      setIsLoading(true);
      try {
        const response = await oikosApi.get(`/?${qs.stringify({
          action: 'get',
          table: 'pedidoCompra',
          data: { pedidoCompra_id: pedidoCompraId },
        })}`);
        if (cancelled) return;

        const row = response?.data?.[0] || currentEditRow || {};
        setDescricao(row.pedidoCompra_desc || '');
        setValorEstimado(row.pedidoCompra_vlrLimite || 0);
        setTipoOrdemCompra(parseTipoOrdemCompra(row.pedidoCompra_comContrato));

        const hasOrc = Boolean(row.orcItem_id);
        setAssociarItemOrcamentario(hasOrc);
        if (hasOrc) {
          setPedidoCompraFields({
            centroDeCustoRubr_id: row.centroDeCustoRubr_id || null,
            orcItem_id: row.orcItem_id || null,
            pedidoCompra_dtVigIni: row.pedidoCompra_dtVigIni || null,
            pedidoCompra_dtVigFim: row.pedidoCompra_dtVigFim || null,
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error(`Erro ao carregar ${entityLabel}:`, error);
        setDescricao(currentEditRow?.pedidoCompra_desc || '');
        setValorEstimado(currentEditRow?.pedidoCompra_vlrLimite || 0);
        notifyError(`Erro ao carregar ${entityLabel}.`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [
    isOpen,
    editPedidoCompraId,
    initialOrcItemId,
    initialCentroDeCustoId,
    resolvedInitialTipo,
    entityLabel,
    clearOrcamentarioFields,
    getOrcDots,
  ]);

  const handleCentroDeCustoChange = async (option) => {
    const centroId = option?.value ?? null;
    setPedidoCompraFields((prev) => ({
      ...prev,
      centroDeCustoRubr_id: centroId,
      orcItem_id: null,
      pedidoCompra_dtVigIni: null,
      pedidoCompra_dtVigFim: null,
    }));
    setSelOrcItems([]);
    setListaDotacoes([]);
    setValoresLimites([]);
    if (!centroId) return;

    try {
      setIsLoadingOrcItems(true);
      const options = await fetchOrcItemsForCentroDeCusto(centroId);
      setSelOrcItems(Array.isArray(options) ? options : []);
    } catch (error) {
      console.error('Erro ao carregar itens orçamentários:', error);
      notifyError('Erro ao carregar itens orçamentários.');
      setSelOrcItems([]);
    } finally {
      setIsLoadingOrcItems(false);
    }
  };

  const resumoOrcamentoPorAno = React.useMemo(() => (
    skipVigenciaDates
      ? buildResumoOrcamentoAnoVigente({ listaDotacoes, valoresLimites })
      : buildResumoOrcamentoPorAno({
        dtIni: pedidoCompraFields.pedidoCompra_dtVigIni,
        dtFim: pedidoCompraFields.pedidoCompra_dtVigFim,
        listaDotacoes,
        valoresLimites,
      })
  ), [
    pedidoCompraFields.pedidoCompra_dtVigIni,
    pedidoCompraFields.pedidoCompra_dtVigFim,
    skipVigenciaDates,
    listaDotacoes,
    valoresLimites,
  ]);

  const hasVigenciaDatesForResumo = skipVigenciaDates
    ? Boolean(pedidoCompraFields.orcItem_id)
    : Boolean(pedidoCompraFields.pedidoCompra_dtVigIni && pedidoCompraFields.pedidoCompra_dtVigFim);

  const vigenciaDatasInvalidas = React.useMemo(() => {
    if (skipVigenciaDates) return false;
    return isVigenciaDatasInvalidas(
      pedidoCompraFields.pedidoCompra_dtVigIni,
      pedidoCompraFields.pedidoCompra_dtVigFim,
    );
  }, [
    pedidoCompraFields.pedidoCompra_dtVigIni,
    pedidoCompraFields.pedidoCompra_dtVigFim,
    skipVigenciaDates,
  ]);

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  // Um item orçamentário com categGr_id = Equipe representa uma vaga, não uma cotação.
  // Nesse caso o formulário correto é o mesmo usado no fluxo de "Cadastrar vaga"
  // (ModalPedidoCompra), então delegamos a renderização para ele em vez de duplicar
  // os campos de vaga aqui.
  const selectedOrcItemOption = React.useMemo(
    () => selOrcItems.find(
      (item) => Number(item.value) === Number(pedidoCompraFields.orcItem_id),
    ) || null,
    [selOrcItems, pedidoCompraFields.orcItem_id],
  );
  const isVagaSelecionada = !readOnly
    && associarItemOrcamentario
    && Number(selectedOrcItemOption?.categGr_id) === EQUIPE_CATEG_GR_ID;

  const vagaInitialPedidoCompraData = React.useMemo(() => ({
    pedidoCompra_desc: descricao,
    pedidoCompra_vlrLimite: valorEstimado,
    orcItem_id: pedidoCompraFields.orcItem_id,
    centroDeCustoRubr_id: pedidoCompraFields.centroDeCustoRubr_id,
    pf_id: Number(selectedOrcItemOption?.pf_id) || undefined,
  }), [
    descricao,
    valorEstimado,
    pedidoCompraFields.orcItem_id,
    pedidoCompraFields.centroDeCustoRubr_id,
    selectedOrcItemOption?.pf_id,
  ]);

  const validateAssociacaoOrcamentaria = () => {
    if (!hideCentroDeCusto && !pedidoCompraFields.centroDeCustoRubr_id) {
      notifyError('Selecione o centro de custo.');
      return false;
    }
    if (!pedidoCompraFields.orcItem_id) {
      notifyError('Selecione o item orçamentário.');
      return false;
    }
    const orcItemPfId = Number(
      selectedOrcItemOption?.pf_id
      || selOrcItems.find((item) => Number(item.value) === Number(pedidoCompraFields.orcItem_id))?.pf_id,
    ) || 0;
    if (!orcItemPfId) {
      notifyError('Item orçamentário sem responsável');
      return false;
    }
    if (!skipVigenciaDates && !pedidoCompraFields.pedidoCompra_dtVigIni) {
      notifyError('Preencha a data de início da vigência.');
      return false;
    }
    if (!skipVigenciaDates && !pedidoCompraFields.pedidoCompra_dtVigFim) {
      notifyError('Preencha a data de fim da vigência.');
      return false;
    }
    if (
      !skipVigenciaDates
      && moment(pedidoCompraFields.pedidoCompra_dtVigFim)
        .isBefore(moment(pedidoCompraFields.pedidoCompra_dtVigIni), 'day')
    ) {
      notifyError('A data de fim deve ser posterior à data de início.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (readOnly) return;

    if (!descricao.trim()) {
      notifyError(`Preencha a descrição do ${entityLabel}.`);
      return;
    }

    if (!(Number(valorEstimado) > 0)) {
      notifyError('Preencha o valor estimado.');
      return;
    }

    if (tipoOrdemCompra !== 0 && tipoOrdemCompra !== 1) {
      notifyError('Selecione o tipo de ordem de compra.');
      return;
    }

    if (associarItemOrcamentario && !validateAssociacaoOrcamentaria()) return;

    const cotacoesParaSalvar = [...cotacoesManuais];
    if (showManualPropostasSection && cotacaoFormTemDados(cotacaoForm)) {
      if (!validateCotacaoForm(cotacaoForm)) return;
      cotacoesParaSalvar.push(normalizeCotacaoManualEntry(cotacaoForm));
    }

    const shouldSendEmails = showEmailSection
      && Boolean(disparoEmailsRef.current?.hasEmails?.());
    const descricaoPedido = descricao.trim();

    try {
      setIsSubmitting(true);

      const response = await oikosApi.post('/', {
        action: 'insert',
        table: 'pedidoCompra',
        data: {
          pedidoCompra_desc: descricaoPedido,
          pedidoCompra_pedidoCotacao: isPedidoCompra ? 0 : 1,
          pedidoCompraTp_id: PEDIDO_COMPRA_TP_BY_TIPO[tipoContratacao],
          pedidoCompra_comContrato: tipoOrdemCompra === 1 ? 1 : 0,
          pedidoCompraStatus_id: PEDIDO_COMPRA_STATUS_APROVADO,
          pedidoCompra_dtSolicitacao: moment().format('YYYY-MM-DD'),
          pedidoCompra_vlrLimite: valorEstimado || 0,
          ...(associarItemOrcamentario ? {
            orcItem_id: pedidoCompraFields.orcItem_id,
            pf_id: Number(
              selectedOrcItemOption?.pf_id
              || selOrcItems.find((item) => (
                Number(item.value) === Number(pedidoCompraFields.orcItem_id)
              ))?.pf_id,
            ) || undefined,
            ...(skipVigenciaDates ? {} : {
              pedidoCompra_dtVigIni: pedidoCompraFields.pedidoCompra_dtVigIni,
              pedidoCompra_dtVigFim: pedidoCompraFields.pedidoCompra_dtVigFim,
            }),
          } : {}),
        },
      });

      const newPedidoCompraId = Number(response?.data?.[0]?.pedidoCompra_id) || 0;
      if (!newPedidoCompraId) {
        notifyError(`Erro ao cadastrar ${entityLabel}.`);
        return;
      }

      for (const cotacao of cotacoesParaSalvar) {
        const insertCotacaoResponse = await oikosApi.post('/', {
          action: 'insert',
          table: 'compraCotacao',
          data: {
            pedidoCompra_id: newPedidoCompraId,
            compraCotacao_desc: cotacao.compraCotacao_desc,
            compraCotacao_vlr: cotacao.compraCotacao_vlr || 0,
            compraCotacao_link: cotacao.compraCotacao_link || undefined,
            compraCotacao_nomeInteressado: cotacao.compraCotacao_nomeInteressado || undefined,
            compraStatus_id: COMPRA_STATUS_ABERTO,
            compraCotacao_dtCotacao: moment().format('YYYY-MM-DD'),
          },
        });

        const newCompraCotacaoId = Number(insertCotacaoResponse?.data?.[0]?.compraCotacao_id) || 0;
        if (newCompraCotacaoId && Array.isArray(cotacao.files) && cotacao.files.length > 0) {
          await savePedidoCompraOuVagaDocuments(cotacao.files, newCompraCotacaoId, 'compraCotacao');
        }
      }

      if (shouldSendEmails) {
        const emailsSent = await disparoEmailsRef.current?.sendEmails?.({
          pedidoCompra_id: newPedidoCompraId,
          pedidoCompra_desc: descricaoPedido,
        });
        if (!emailsSent) {
          notifyError(`${entityLabel} salvo, mas os e-mails não puderam ser enviados.`);
        }
      }

      await onSuccess?.({
        pedidoCompra_id: newPedidoCompraId,
        pedidoCompra_pedidoCotacao: isPedidoCompra ? 0 : 1,
      });
      notifySuccess(`${entityLabel} cadastrado com sucesso.`);
      resetState();
      onClose();
    } catch (error) {
      console.error(`Erro ao cadastrar ${entityLabel}:`, error);
      notifyError(`Erro ao cadastrar ${entityLabel}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formDisabled = isSubmitting || readOnly;

  if (isVagaSelecionada) {
    return (
      <ModalPedidoCompra
        isOpen={isOpen}
        onClose={handleClose}
        initialPedidoCompraData={vagaInitialPedidoCompraData}
        selCentroDeCusto={selCentroDeCusto}
        selOrcItems={selOrcItems}
        hideCentroDeCusto={hideCentroDeCusto}
        readOnlyOrcItem
        hidePropItem
        hideQuantidadeOportunidades
        hideDtLimite
        showSkipCotacao={false}
        user={user}
        onSuccess={onSuccess}
        title="Cadastrar vaga"
        submitLabel="Adicionar vaga"
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl">
      <ModalHeader toggle={handleClose}>
        <Typography id="modal-pedido-cotacao-title" variant="h6" component="h2">
          {title}
        </Typography>
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            {!readOnly && !lockOrcItemAssociation && (
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={associarItemOrcamentario}
                    disabled={formDisabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAssociarItemOrcamentario(checked);
                      if (!checked) {
                        clearOrcamentarioFields();
                      }
                    }}
                  />
                  {' '}
                  Desejo associar a um item orçamentário
                </Label>
              </FormGroup>
            )}

            {associarItemOrcamentario && (
              <>
                <Row>
                  {!hideCentroDeCusto && (
                    <Col sm={4}>
                      <FormGroup>
                        <Label>Centro de custo</Label>
                        <Select
                          classNamePrefix="select2-selection"
                          placeholder="Selecione um centro de custo"
                          isClearable
                          isDisabled={formDisabled}
                          options={selCentroDeCusto}
                          value={selCentroDeCusto.filter((item) => (
                            Number(item.value) === Number(pedidoCompraFields.centroDeCustoRubr_id)
                          ))}
                          onChange={handleCentroDeCustoChange}
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col sm={hideCentroDeCusto ? 12 : 8}>
                    <FormGroup>
                      <Label>Item orçamentário</Label>
                      <Select
                        classNamePrefix="select2-selection"
                        placeholder={isLoadingOrcItems ? 'Carregando...' : 'Selecione um item'}
                        isClearable={!readOnlyOrcItem}
                        isDisabled={
                          formDisabled
                          || readOnlyOrcItem
                          || (!hideCentroDeCusto && !pedidoCompraFields.centroDeCustoRubr_id)
                          || isLoadingOrcItems
                        }
                        isLoading={isLoadingOrcItems}
                        options={selOrcItems}
                        value={selOrcItems.filter((item) => (
                          Number(item.value) === Number(pedidoCompraFields.orcItem_id)
                        ))}
                        onChange={(option) => {
                          const newOrcItemId = option?.value ?? null;
                          if (newOrcItemId && !Number(option?.pf_id)) {
                            notifyError('Item orçamentário sem responsável');
                            return;
                          }
                          setPedidoCompraFields((prev) => ({
                            ...prev,
                            orcItem_id: newOrcItemId,
                            pedidoCompra_dtVigIni: null,
                            pedidoCompra_dtVigFim: null,
                          }));
                          getOrcDots(newOrcItemId);
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {pedidoCompraFields.orcItem_id ? (
                  <>
                    {!skipVigenciaDates && (
                    <Row>
                      <Col sm={4}>
                        <FormGroup>
                          <Label>Data Início Vigência</Label>
                          <DatePicker
                            placeholderText="__/__/_____"
                            locale="pt-BR"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            disabled={formDisabled}
                            selected={
                              pedidoCompraFields.pedidoCompra_dtVigIni
                                ? moment(pedidoCompraFields.pedidoCompra_dtVigIni).toDate()
                                : null
                            }
                            onChange={(date) => {
                              if (formDisabled) return;
                              setPedidoCompraFields((prev) => ({
                                ...prev,
                                pedidoCompra_dtVigIni: date
                                  ? moment(date).format('YYYY-MM-DD')
                                  : null,
                              }));
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col sm={4}>
                        <FormGroup>
                          <Label>Data Fim Vigência</Label>
                          <DatePicker
                            placeholderText="__/__/_____"
                            locale="pt-BR"
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            disabled={formDisabled}
                            selected={
                              pedidoCompraFields.pedidoCompra_dtVigFim
                                ? moment(pedidoCompraFields.pedidoCompra_dtVigFim).toDate()
                                : null
                            }
                            onChange={(date) => {
                              if (formDisabled) return;
                              setPedidoCompraFields((prev) => ({
                                ...prev,
                                pedidoCompra_dtVigFim: date
                                  ? moment(date).format('YYYY-MM-DD')
                                  : null,
                              }));
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    )}

                    {!readOnly && (
                      <Row>
                        <Col sm={12}>
                          {hasVigenciaDatesForResumo ? (
                            vigenciaDatasInvalidas ? (
                              <div className="mb-2">
                                <span style={{ fontWeight: 'bold' }}>Datas de início e/ou fim erradas.</span>
                              </div>
                            ) : (
                              <div className="mb-2 d-flex flex-wrap">
                                {resumoOrcamentoPorAno.map(({
                                  year,
                                  somaDotacoes,
                                  valorDisponivel,
                                  jaAlocados,
                                }) => (
                                  <div key={year} className="d-flex flex-column mr-4 mb-2">
                                    <strong className="mb-1">{year}</strong>
                                    <span>
                                      Valor disponível:{' '}
                                      <span style={{ fontWeight: 'bold' }}>
                                        {currencyFormatter.format(valorDisponivel)}
                                      </span>
                                    </span>
                                    <span>
                                      Soma de dotações para esse item:{' '}
                                      <span style={{ fontWeight: 'bold' }}>
                                        {currencyFormatter.format(somaDotacoes)}
                                      </span>
                                    </span>
                                    <span>
                                      Já alocados:{' '}
                                      <span style={{ fontWeight: 'bold' }}>
                                        {currencyFormatter.format(jaAlocados)}
                                      </span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            <div className="mb-2">
                              <span style={{ fontWeight: 'bold' }}>
                                Selecione uma data de início e fim de vigência
                              </span>
                            </div>
                          )}
                        </Col>
                      </Row>
                    )}
                  </>
                ) : null}
              </>
            )}

            {showTipoRadios && (
            <FormGroup tag="fieldset" className="mb-3">
              <div className="d-flex flex-wrap align-items-center">
                <FormGroup check inline className="mb-0 mr-4">
                  <Label check>
                    <Input
                      type="radio"
                      name="tipoContratacaoPedidoCotacao"
                      checked={tipoContratacao === TIPO_CONTRATACAO.PADRAO}
                      disabled={formDisabled}
                      onChange={() => {
                        setTipoContratacao(TIPO_CONTRATACAO.PADRAO);
                        setCotacaoForm(createEmptyCotacaoForm());
                        setCotacoesManuais([]);
                      }}
                    />
                    {' '}
                    Cotação padrão
                  </Label>
                </FormGroup>
                <FormGroup check inline className="mb-0 mr-4">
                  <Label check>
                    <Input
                      type="radio"
                      name="tipoContratacaoPedidoCotacao"
                      checked={tipoContratacao === TIPO_CONTRATACAO.RAPIDA}
                      disabled={formDisabled}
                      onChange={() => {
                        setTipoContratacao(TIPO_CONTRATACAO.RAPIDA);
                        setCotacaoForm(createEmptyCotacaoForm());
                        setCotacoesManuais([]);
                        disparoEmailsRef.current?.reset?.();
                      }}
                    />
                    {' '}
                    Cotação rápida
                  </Label>
                </FormGroup>
                <FormGroup check inline className="mb-0">
                  <Label check>
                    <Input
                      type="radio"
                      name="tipoContratacaoPedidoCotacao"
                      checked={tipoContratacao === TIPO_CONTRATACAO.ADESAO}
                      disabled={formDisabled}
                      onChange={() => {
                        setTipoContratacao(TIPO_CONTRATACAO.ADESAO);
                        setCotacaoForm(createEmptyCotacaoForm());
                        setCotacoesManuais([]);
                      }}
                    />
                    {' '}
                    Cotação por adesão
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>
            )}

            <FormGroup>
              <Label>Tipo de ordem de compra</Label>
              <Select
                classNamePrefix="select2-selection"
                placeholder="Selecione"
                isClearable
                isDisabled={formDisabled}
                options={PEDIDO_COMPRA_COM_CONTRATO_OPTIONS}
                value={PEDIDO_COMPRA_COM_CONTRATO_OPTIONS.filter((item) => (
                  item.value === tipoOrdemCompra
                ))}
                onChange={(option) => {
                  setTipoOrdemCompra(
                    option?.value === 0 || option?.value === 1 ? option.value : null,
                  );
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Descrição</Label>
              <Input
                type="textarea"
                rows={4}
                placeholder={`Descreva o ${entityLabel}`}
                value={descricao}
                disabled={formDisabled}
                readOnly={readOnly}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Valor estimado</Label>
              <MaskedInput
                unmask
                mask="R$ num"
                disabled={formDisabled}
                blocks={{
                  num: {
                    mask: Number,
                    scale: 2,
                    signed: false,
                    thousandsSeparator: '.',
                    padFractionalZeros: false,
                    normalizeZeros: false,
                    radix: ',',
                    min: 0,
                    max: 1000000000,
                  },
                }}
                lazy={false}
                value={String(valorEstimado ?? '')}
                onAccept={(value) => {
                  if (!formDisabled) {
                    setValorEstimado(value);
                  }
                }}
              />
            </FormGroup>

            {!readOnly && showManualPropostasSection && (
              <CotacaoRapidaPropostasSection
                cotacaoForm={cotacaoForm}
                setCotacaoForm={setCotacaoForm}
                cotacoesManuais={cotacoesManuais}
                onChangeCotacoesManuais={setCotacoesManuais}
                disabled={formDisabled}
                showAnexos
                idPrefix="pedido_cotacao"
                deferralHint={`Você também pode abrir ${isPedidoCompra ? 'o pedido de compra' : 'a cotação'} agora e incluir propostas depois.`}
              />
            )}

            {!readOnly && showEmailSection && isOpen && (
              <DisparoEmailsCotacaoForm
                ref={disparoEmailsRef}
                disabled={formDisabled}
                hideBancoFornecedores
                hideLinkCompartilhamentoAberto
                pedidoCompraData={{
                  pedidoCompra_id: Number(editPedidoCompraRow?.pedidoCompra_id) || undefined,
                  pedidoCompra_desc: descricao,
                }}
                fetchPedidoCompraDocuments={fetchPedidoCompraDocuments}
              />
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Fechar
        </Button>
        {!readOnly && (
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? <Spinner size="sm" /> : submitLabel}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export {
  TIPO_CONTRATACAO,
  TIPO_CONTRATACAO_LABEL,
};

export default ModalPedidoCotacao;
